import { Signal } from 'typed-signals';
import type { App } from '../../../../../app/App';
import { getLivesCoinCost } from '../../../../../app/state/lives/getLivesCoinCost';
import { getLivesToRefill } from '../../../../../app/state/lives/getLivesToRefill';
import { soundManager } from '../../../../../app/SoundManager';
import type { BuyLivesButtonLayout } from '../../../../../libs/layouts/types/LayoutShapes';
import { IAPPlacement } from '../../../../../app/iap/types';
import { openShopPopup } from '../../../../../behaviours/openShopPopup';

// Using centralized BuyLivesButtonLayout

export class BuyLivesButtonController {
  #enabled = true;

  public readonly onLivesPurchased = new Signal<() => void>();

  constructor(
    private readonly app: App,
    private readonly layout: BuyLivesButtonLayout,
  ) {
    layout.livesButtonContainer.on('pointertap', () => {
      this.onBuyLivesClick();
    });
  }

  private onBuyLivesClick(): void {
    const { stateController, resources } = this.app;
    const livesToRefill = getLivesToRefill(stateController);
    const livesRefillCost = getLivesCoinCost();
    if (livesRefillCost === 0) return;

    const canAfford: boolean = stateController.canAffordCoinCost(livesRefillCost);
    if (!canAfford) {
      void openShopPopup(this.app, IAPPlacement.UpsellOffer, true);
      return;
    }

    resources.payForLivesRefill(livesToRefill);

    soundManager.play('UI_Button_Click');

    this.onLivesPurchased.emit();
  }

  public update(): void {
    const { stateController, iap } = this.app;
    const { buyLivesAmountText, buyLivesCoinAmountText } = this.layout;
    const currentLives = stateController.getLives();
    const maxLives = stateController.getLivesMax();
    const livesToRefill = getLivesToRefill(stateController);
    const livesRefillCost = getLivesCoinCost();

    buyLivesAmountText.text = `+${livesToRefill}`;
    buyLivesCoinAmountText.text = livesRefillCost;

    const canProceedWithBuying =
      currentLives < maxLives && (!!iap || stateController.canAffordCoinCost(livesRefillCost));

    this.enabled = canProceedWithBuying;
  }

  set enabled(value: boolean) {
    if (this.#enabled === value) return;
    this.#enabled = value;
    this.layout.livesButtonContainer.interactive = this.#enabled;
  }

  get enabled(): boolean {
    return this.#enabled;
  }
}
